import { useQuery } from '@tanstack/react-query'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { matchPath } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectErrorDetailMapFromState,
  selectExpenseReport,
  selectExpenseReportIdFromRoute,
  selectFetchFailureFromState,
  selectPostingExpenseFromState,
  selectReceiptAttachmentModelsList,
  selectTopLevelError,
  selectUploadingAttachmentsFromState,
} from 'src/app/AddExpenseFlyout/redux/addExpense.selectors'
import { createSetExpenseReceiptAttachmentIds } from 'src/app/UpdateExpenseFlyout/redux/updateExpense.actions'
import ExpenseFlyoutHeader from 'src/components/ExpenseFlyoutHeader/ExpenseFlyoutHeader'
import ExpenseForm, {
  ExpenseFields,
} from 'src/components/ExpenseForm/ExpenseForm'
import LoadingRightFlyoutContent from 'src/components/LoadingRightFlyoutContent/LoadingRightFlyoutContent'
import NotFoundRightFlyoutContent from 'src/components/NotFoundRightFlyoutContent/NotFoundRightFlyoutContent'
import RightFlyout, {
  RightFlyoutBodyWidths,
} from 'src/components/RightFlyout/RightFlyout'
import { ExtendCurrency } from 'src/enums'
import { useFlyoutDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { ExpenseReportStatus } from 'src/models/expenseReport.model'
import { expenseReportUrl } from 'src/navigation/urls'
import {
  isOutOfPocketExpensesVisible,
  orgOutOfPocketExpensesQueries,
} from 'src/queries/organizations/outOfPocketExpenses/outOfPocketExpenses.queries'
import { useUploadReceiptAttachments } from 'src/queries/receiptAttachments/receiptAttachments.queries'
import {
  getExpenseReport,
  postCreateExpense,
} from 'src/redux/actions/data/expenses.actions'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { toCents } from 'src/utils/currency.utils'

const AddExpenseFlyout: React.FC = () => {
  const translate = useTranslate()

  const dispatch = useFlyoutDispatch()
  const uploadReceiptAttachments = useUploadReceiptAttachments()
  const fetchFailure = useSelector(selectFetchFailureFromState)
  const expenseReportId = useSelector(selectExpenseReportIdFromRoute)
  const expenseReport = useSelector(selectExpenseReport)
  const errorDetailMap = useSelector(selectErrorDetailMapFromState)
  const topLevelError = useSelector(selectTopLevelError)
  const postingExpense = useSelector(selectPostingExpenseFromState)
  const receiptAttachments = useSelector(selectReceiptAttachmentModelsList)
  const uploadingAttachmentThumbnails = useSelector(
    selectUploadingAttachmentsFromState,
  )
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const { data: hasOutOfPocketExpenses } = useQuery(
    orgOutOfPocketExpensesQueries.enabled(organization?.id),
  )
  const outOfPocketExpensesEnabled = isOutOfPocketExpensesVisible(
    organization,
    hasOutOfPocketExpenses,
  )
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const isExpenseReportPage =
    matchPath(
      calculatedBackgroundLocation.pathname,
      expenseReportUrl(expenseReportId),
    ) !== null
  const { analytics } = useAnalyticsContext()

  const dispatchUploadAttachments = (
    attachments: PostReceiptAttachmentRequest[],
  ): void => {
    uploadReceiptAttachments.mutate(attachments)
  }

  const deleteAttachment = (attachmentId: string): void => {
    dispatch(
      createSetExpenseReceiptAttachmentIds(
        receiptAttachments
          .filter((att) => att.id !== attachmentId)
          .map((att) => att.id),
      ),
    )
  }

  const handleScroll = (): void => {
    document.getElementById('flyout')?.scrollIntoView()
  }

  const dispatchPostCreateExpense = (
    body: ExpenseFields,
    amountToBeReimbursed: number,
    isExpenseTypeMileage: boolean,
  ): void => {
    const payload: Record<string, unknown> = {
      ...body,
      amountCents: isExpenseTypeMileage
        ? toCents(amountToBeReimbursed)
        : body.amountCents !== undefined
          ? toCents(body.amountCents)
          : undefined,
      expenseReportId: expenseReportId,
      currencyCode: ExtendCurrency.Usd,
      receiptAttachmentIds: receiptAttachments.map((att) => att.id),
      mileageRate: isExpenseTypeMileage
        ? (organization?.mileageRateThousandths ?? 0) * 1000
        : undefined,
    }
    if (isExpenseTypeMileage) {
      payload.useMileageRoute = body.useMileageRoute ?? false
      if (body.useMileageRoute) {
        payload.distance = null
      } else {
        payload.mileageRoute = null
      }
    }
    dispatch(postCreateExpense(payload as ExpenseFields, isExpenseReportPage))
    analytics?.track('Successfully create OOPE', {
      expense_type: body.type === 'MILEAGE' ? 'Mileage' : 'General',
      amount_spent: body.amountCents ? body.amountCents : undefined,
      distance: body.distance ?? body.mileageRoute?.distance,
      expense_name: expenseReport?.reportName,
      receipt_attached: receiptAttachments && receiptAttachments.length > 0,
      add_notes: !!body.notes,
      ...(isExpenseTypeMileage && {
        mileage_calculation_method: body.useMileageRoute
          ? 'google_maps'
          : 'manual',
      }),
      ...(isExpenseTypeMileage &&
        body.useMileageRoute && {
          stops_count: body.mileageRoute?.stops?.length ?? 0,
        }),
    })
  }

  useEffect((): void => {
    dispatch(getExpenseReport(expenseReportId))
  }, [expenseReportId])

  return (
    <RightFlyout width={RightFlyoutBodyWidths.Action}>
      {outOfPocketExpensesEnabled &&
      expenseReport !== undefined &&
      expenseReport.status === ExpenseReportStatus.Draft ? (
        <>
          <span id={'flyout'}>
            <ExpenseFlyoutHeader
              reportName={expenseReport.reportName}
              flyoutTitle={'Add New Expense'}
            />
            <ExpenseForm
              expenseReportId={expenseReportId}
              isSubmitter={true}
              handleScroll={handleScroll}
              loading={postingExpense}
              deleteAttachment={deleteAttachment}
              errorDetailMap={errorDetailMap}
              receiptAttachments={receiptAttachments}
              uploadAttachments={dispatchUploadAttachments}
              uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
              topLevelError={topLevelError}
              buttonText={translate('Save and Review')}
              notesAndReceiptsEditable
              fieldsEditable
              mileageRate={organization?.mileageRateThousandths}
              onSuccessfulSubmit={dispatchPostCreateExpense}
            />
          </span>
        </>
      ) : fetchFailure ? (
        <NotFoundRightFlyoutContent />
      ) : (
        <LoadingRightFlyoutContent />
      )}
    </RightFlyout>
  )
}

export default AddExpenseFlyout
